import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Styles } from "./styled";

export interface PrintTablePDFProps {
  columns: any[];
  rows: any[];
  className?: string;
}

export interface PrintTablePDFHandle {
  print: () => void;
}

const PrintTablePDF = forwardRef<PrintTablePDFHandle, PrintTablePDFProps>(
  ({ columns, rows, className = "" }, ref) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({ contentRef });

    useImperativeHandle(ref, () => ({
      print: handlePrint,
    }));

    const jpFont =
      '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", "Yu Gothic", Meiryo, sans-serif';

    const pdfHeader = useMemo(() => {
      return (
        columns?.map((col: any) => col.label || String(col.columnId)) || []
      );
    }, [columns]);

    const pdfRows = useMemo(() => {
      return (
        rows
          ?.filter(
            (row: any) =>
              row.rowId !== "header" &&
              row.rowId !== "total" &&
              row.rowId !== "hidden" &&
              !row.rowId?.toString().startsWith("config_"),
          )
          ?.map((row: any) => {
            return columns?.map((col: any, cIdx: number) => {
              const cell = row.cells?.[cIdx] || {};
              let val =
                cell.text ||
                cell.value ||
                cell.richText ||
                cell.date ||
                cell.time ||
                cell.number ||
                "";
              if (typeof val === "boolean") val = val ? "Yes" : "No";
              if (Array.isArray(val)) val = val.join(", ");
              if (typeof val === "object" && val !== null) {
                val = val.text || val.name || val.label || JSON.stringify(val);
              }
              return val === "[]" || val === "{}" ? "" : val;
            });
          }) || []
      );
    }, [columns, rows]);

    return (
      <div
        style={{
          position: "absolute",
          overflow: "hidden",
          width: 0,
          height: 0,
        }}
      >
        <Styles
          ref={contentRef}
          className={className}
          style={{ padding: "20px" }}
        >
          <table className="print-table" style={{ fontFamily: jpFont }}>
            <thead>
              <tr>
                {pdfHeader.map((col, index) => (
                  <th key={index} style={{ fontFamily: jpFont }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pdfRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td key={cellIndex} style={{ fontFamily: jpFont }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Styles>
      </div>
    );
  },
);

PrintTablePDF.displayName = "PrintTablePDF";

export default PrintTablePDF;
